import React, {type ReactNode, useEffect, useId, useMemo, useRef, useState} from 'react'
import {Head} from '@inertiajs/react'
import {
    CalendarIcon,
    CheckCircleIcon,
    ClipboardDocumentCheckIcon,
    ClipboardDocumentListIcon,
    ClockIcon,
    CreditCardIcon,
    MapPinIcon,
    TrophyIcon,
    UserIcon,
    UsersIcon,
} from '@heroicons/react/20/solid'
import axios from 'axios'
import {route} from 'ziggy-js'
import KeyValuePair from '@/Library/Classes/KeyValuePair'
import Input from '@/Components/Forms/Input'
import PhoneNumberInput from '@/Components/Forms/PhoneNumberInput'
import InputErrorLabel from '@/Components/Forms/InputErrorLabel'
import SelectDropdown from '@/Components/Forms/SelectDropdown'
import SidebarToggle from '@/Components/Sidebars/Form/SidebarToggle'
import Button from '@/Components/Buttons/Button'
import Logo from '@/Components/Assets/Logo'
import Badge from '@/Components/Assets/Badge'
import TermsDisclosure from '@/Components/TermsDisclosure'
import classNames from '@/Utils/ClassNames'
import {firstValidationErrors} from '@/Utils/AxiosValidation'
import {formatCurrency} from '@/Utils/Currency'
import {formatShortDateRange} from '@/Utils/DateTime'
import {OPTION_INPUT_CLASS} from '@/Utils/FormStyles'
import {useTurnstile} from '@/Utils/Hooks/useTurnstile'
import {usePaymentComponent} from '@/Utils/Hooks/usePaymentComponent'
import type {ApprovedPaymentDetail, PaymentComponentDescriptor, PaymentSdkDescriptor} from '@/Types/PaymentComponent'
import {type RegistrationFormField, type RegistrationFormFieldKey} from '@/Types/RegistrationForm'
import {
    addressSummary,
    applyPrefill,
    coachSummary,
    customFieldDisplayLabel,
    customFieldsComplete,
    type PrefillAddress,
    type PrefillTeam,
    resolvedTeamClassificationId as resolveTeamClassificationId,
    soleTeamClassificationId as soleClassificationForTeamAge,
    teamStepFieldsComplete,
    teamSummary,
    tournamentSelectionSummary,
} from '@/Utils/RegistrationForms'
import {usaStates} from 'typed-usa-states'
import {
    AgeGroupCardGroup,
    ClassificationCardGroup,
    formatRegisteredCount,
    isAlreadyPaid,
    isAlreadyRegistered,
    isUnpaidRegistration,
    resolvedAgeId,
    resolvedClassificationId,
    resolvedClassificationName,
    type Tournament,
    type TournamentPick,
} from '@/Pages/Public/Registration/TournamentDivisionPicker'
import LookingToPayDialog from '@/Pages/Public/Registration/LookingToPayDialog'
import './RegistrationForm.css'

interface Props {
    form: {
        id: string
        name: string
    }
    fields: RegistrationFormField[]
    field_descriptions: Partial<Record<RegistrationFormFieldKey, string>>
    logo_url: string | null
    organization_name: string
    pay_later_enabled: boolean
    /** Read by app.tsx's preloadPaymentSdk() on navigate, not by the page (ENG-4688). */
    payment_sdk: PaymentSdkDescriptor | null
    team_categories: {
        id: string
        name: string
        ages: {id: string, name: string}[]
        classifications: {id: string, name: string}[]
    }[]
}

type FieldValue = string | boolean | string[]

/**
 * `offered` greets a matched coach by name and asks whether to prefill
 * (ENG-4059) — Yes is what sends the code, so the OTP only ever runs for
 * someone who actually wants their stored details back. `idle` covers both
 * ends of the widget's life: never offered, and done with by any route (said
 * No, verified, or gave up on the code), which unmounts it. Re-submitting
 * the Coach step is what offers it again.
 */
type VerificationStatus = 'idle' | 'offered' | 'sent'

type VerificationCheckResponse = {
    verified: boolean
    team: PrefillTeam | null
    address: PrefillAddress | null
}

type Screen = 'form' | 'payment'

type PayChoice = 'now' | 'later'

type SectionKey = 'coach' | 'team' | 'address'

type SectionState = 'locked' | 'active' | 'completed'

const RESEND_COOLDOWN_MS = 60000
const MAIN_CLASS = 'registration-form mx-auto flex w-full max-w-2xl flex-col gap-6 p-4 pb-16'

// The verification widget unmounts the moment the coach is done with it, so
// these stand in for the confirmation copy it used to leave behind — sighted
// users see the Team fields (filled or blank), everyone else gets this.
const PREFILLED_ANNOUNCEMENT = 'Your team and address have been prefilled below.'
const MANUAL_ENTRY_ANNOUNCEMENT = 'Continue by entering your team and address below.'

/**
 * Every button on this page wants the neutral-grey disabled treatment
 * (ENG-4017 follow-up), so it's the default here rather than repeated at
 * each call site.
 */
function RegistrationButton(props: React.ComponentProps<typeof Button>): ReactNode {
    return <Button mutedDisabled {...props}/>
}

function scrollToSection(ref: React.RefObject<HTMLHeadingElement | null>): void {
    ref.current?.focus()
    ref.current?.scrollIntoView({behavior: 'smooth'})
}

function toOptions(items: {id: string, name: string}[]): KeyValuePair[] {
    return items.map((item) => new KeyValuePair(item.id, item.name))
}

/**
 * One bullet per tournament rather than a run-on "A, B and C" sentence
 * (ENG-4030) — each bullet gets its date range, and (for tournaments still
 * awaiting payment) its registration due date, matching the "Due <date>"
 * subline convention from `RegistrationPaymentLink.tsx`.
 */
function TournamentConfirmationBullets({tournaments, showDueDate, label}: {tournaments: Tournament[], showDueDate: boolean, label?: string}): ReactNode {
    return <ul aria-label={label} className={'flex flex-col gap-2'}>
        {tournaments.map((tournament) => (
            <li
                key={tournament.id}
                data-testid={`registration-payment-confirmation-tournament-${tournament.id}`}
                className={'rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5'}
            >
                <p className={'font-semibold text-gray-900'}>{tournament.name}</p>
                <p className={'mt-1 flex items-center gap-1.5 text-gray-600'}>
                    <CalendarIcon aria-hidden={'true'} className={'size-4 shrink-0'}/>
                    {formatShortDateRange(tournament.starts_at, tournament.ends_at)}
                </p>
                {showDueDate && tournament.payment_due_at != null && (
                    <p
                        className={'mt-1 flex items-center gap-1.5 text-gray-600'}
                        data-testid={`registration-payment-confirmation-due-${tournament.id}`}
                    >
                        <ClockIcon aria-hidden={'true'} className={'size-4 shrink-0'}/>
                        {`Due ${formatShortDateRange(tournament.payment_due_at, null)}`}
                    </p>
                )}
            </li>
        ))}
    </ul>
}

/**
 * Thank-you copy varies by what actually ends up paid vs. invoiced. Pay
 * Later always defers everything (see `submitRegistration`), so in practice
 * this only varies for a Pay Now submission with some tournaments deferred.
 */
function PaymentConfirmation({paidTournaments, deferredTournaments}: {paidTournaments: Tournament[], deferredTournaments: Tournament[]}): ReactNode {
    if (deferredTournaments.length === 0) {
        return <>
            <p className={'flex items-center gap-2 text-base font-semibold text-gray-900'}>
                <CheckCircleIcon aria-hidden={'true'} className={'size-5 shrink-0 text-shamrock-30'}/>
                Thanks! You're registered and paid for:
            </p>
            <TournamentConfirmationBullets tournaments={paidTournaments} showDueDate={false}/>
            <p className={'text-gray-500'}>We'll email your receipt shortly.</p>
        </>
    }

    if (paidTournaments.length === 0) {
        return <>
            <p className={'text-base font-semibold text-gray-900'}>Thanks! Your registration is submitted for:</p>
            <TournamentConfirmationBullets tournaments={deferredTournaments} showDueDate={true}/>
            <p className={'text-gray-500'}>We just sent you a confirmation text and email with your payment link.</p>
        </>
    }

    return <>
        <p className={'flex items-center gap-2 text-base font-semibold text-gray-900'}>
            <CheckCircleIcon aria-hidden={'true'} className={'size-5 shrink-0 text-shamrock-30'}/>
            Thanks! You're paid for:
        </p>
        <TournamentConfirmationBullets tournaments={paidTournaments} showDueDate={false} label={'Paid tournaments'}/>
        <p className={'text-base font-semibold text-gray-900'}>We just sent you a confirmation text and email with your payment link for the following:</p>
        <TournamentConfirmationBullets tournaments={deferredTournaments} showDueDate={true} label={'Invoiced tournaments'}/>
    </>
}

// Sections downstream of Coach, in order — Team/Address/Tournament Selection
// re-lock in this order whenever an earlier one is edited. Additional
// Questions/Submit are always implied by "and later sections" rather than
// listed by name, since they never carry their own saved state to lose.
const DOWNSTREAM_SECTION_LABELS = ['Team', 'Address', 'Tournament Selection']

/**
 * Screen-reader announcement for a cascading re-lock: editing an
 * already-saved section instantly re-locks everything after it (see the
 * `xState` expressions below), which is a bigger context change than a
 * normal field edit and needs its own call-out. `fromIndex` is how far into
 * `DOWNSTREAM_SECTION_LABELS` the *changed* section's own downstream begins
 * (0 for Coach, 1 for Team, 2 for Address).
 */
function cascadeMessage(changedSection: string, fromIndex: number): string {
    const downstream = DOWNSTREAM_SECTION_LABELS.slice(fromIndex)
    const suffix = downstream.length > 1 ? `${downstream.join(', ')},` : downstream.join(', ')

    return `${changedSection} details changed — ${suffix} and later sections must be reviewed again.`
}

/**
 * Fixed, code-based blocks applied to every form (Coach, Team, Address,
 * Tournament Selection) plus operator-authored custom fields, rendered here
 * as a static scaffold. The Coach block — including the required TCPA/SMS
 * consent toggle — matches/creates a Person and logs consent on submit
 * (ENG-3859, ENG-3860). A matched coach is offered SMS/email OTP
 * verification; on success their most recent Team + Address (scoped to this
 * form's organization) are prefilled within this flow only — no session is
 * created (ENG-3861). The Team block requires a Category first — labeled
 * "Sport" here since that's the term coaches expect, though the underlying
 * field/model is still Category — it scopes which Ages/Classifications are
 * offered and is part of the fuzzy match (normalized name compare) key,
 * since the same team name under a different sport is always a different
 * Team. Only Categories with at least one tournament open for registration
 * reach `team_categories` at all (ENG-4060), so the dropdown never offers a
 * sport that would dead-end at an empty Tournament Selection step; when none
 * qualify the dropdown is replaced by a "no tournaments open" message. The
 * block then fuzzy-matches an existing Team for the coach Person or
 * creates a new one, snapshotting the declared
 * Age/Classification onto it (ENG-3862). The Address block requires a saved
 * Team first — it creates or updates that Team's single current Address (a
 * team has exactly one; resubmitting updates it in place rather than
 * creating a new one), enforcing that the Address's organization matches the
 * Team's (ENG-3863). The Tournament Selection block fetches once the Address
 * is saved, listing only the tournaments the saved Team is eligible for —
 * offered (EventAge/EventAgeClassification), eligible (age/classification
 * position at or above the Team's declared level), and open for registration
 * (Live, or Draft with a Registration Starts At date already in the past —
 * ENG-4000 — with registration enabled, its Starts At unset or passed and its Ends At unset or still ahead —
 * ENG-4658) are all enforced server-side by TournamentEligibilityService, not
 * just filtered here.
 * Registered counts are a display-only placeholder until EventRegistrations/
 * EventRegistrationEntry exist (ENG-3867/ENG-3868). Checking a tournament only
 * updates local selection state. "Continue to Payment" swaps in a Payment
 * screen — a `screen` state toggle rather than real navigation, so Back is a
 * client-side no-op until the coach actually submits. That screen lists the
 * selected tournaments (with a pay-now checkbox per tournament when the org's
 * Pay Later flag is on) and posts the code-template fields, the payment
 * choice, and which tournaments are paid now vs. later to the submission
 * endpoint (ENG-3865/ENG-3870), gated on a Cloudflare Turnstile challenge
 * server-verified in that same request (ENG-3866) — see {@see useTurnstile}.
 * That call registers the team against every selected tournament regardless
 * of payment choice (entries are a registration fact, independent of
 * payment); when one or more are paid now, it also returns a
 * paymentComponent descriptor, which swaps the summary/buttons for the
 * Rainforest card widget — approval finalizes the charge against a second
 * endpoint (ENG-3871). Pay Later acts as a cancel-the-payment step — it
 * always submits an empty paid set regardless of which per-tournament
 * checkboxes are still checked, so it never returns a payin config and goes
 * straight to the thank-you message (ENG-3923); only Pay Now can produce a
 * mixed paid-now/deferred submission.
 */
export default function RegistrationForm({form, fields, field_descriptions, logo_url, organization_name, pay_later_enabled, team_categories}: Props): ReactNode {
    // Single-category orgs have nothing to choose — preselect it so the
    // Age/Classification dropdowns are already available on load.
    const [values, setValues] = useState<Record<string, FieldValue>>(
        () => (team_categories.length === 1 ? {team_category_id: team_categories[0].id} : {}),
    )
    const [coachErrors, setCoachErrors] = useState<Record<string, string>>({})
    const [coachSaving, setCoachSaving] = useState(false)
    const [coachSaved, setCoachSaved] = useState(false)
    const [coachPersonId, setCoachPersonId] = useState<string | null>(null)
    const [teamErrors, setTeamErrors] = useState<Record<string, string>>({})
    const [teamSaving, setTeamSaving] = useState(false)
    const [teamSaved, setTeamSaved] = useState(false)
    const [teamId, setTeamId] = useState<string | null>(null)
    const [addressErrors, setAddressErrors] = useState<Record<string, string>>({})
    const [addressSaving, setAddressSaving] = useState(false)
    const [addressSaved, setAddressSaved] = useState(false)
    const [tournaments, setTournaments] = useState<Tournament[]>([])
    const [tournamentsLoading, setTournamentsLoading] = useState(false)
    const [tournamentsError, setTournamentsError] = useState<string | null>(null)
    const [payPromptTournament, setPayPromptTournament] = useState<Tournament | null>(null)
    const [payPromptOpening, setPayPromptOpening] = useState(false)
    const [picks, setPicks] = useState<Record<string, TournamentPick>>({})
    const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>('idle')
    const [verificationChannel, setVerificationChannel] = useState<'sms' | 'email' | null>(null)
    const [verificationErrors, setVerificationErrors] = useState<Record<string, string>>({})
    const [verificationSending, setVerificationSending] = useState(false)
    const [verificationChecking, setVerificationChecking] = useState(false)
    const [resendLockedUntil, setResendLockedUntil] = useState<number | null>(null)
    const [prefillVersion, setPrefillVersion] = useState(0)
    const [screen, setScreen] = useState<Screen>('form')
    const [paidTournamentIds, setPaidTournamentIds] = useState<string[]>([])
    const [status, setStatus] = useState<'idle' | 'submitting' | 'submitted'>('idle')
    const [submitErrors, setSubmitErrors] = useState<Record<string, string>>({})
    const [submissionId, setSubmissionId] = useState<string | null>(null)
    const [paymentConfig, setPaymentConfig] = useState<PaymentComponentDescriptor | null>(null)
    const [openSectionOverride, setOpenSectionOverride] = useState<SectionKey | null>(null)
    const [liveAnnouncement, setLiveAnnouncement] = useState('')
    const turnstile = useTurnstile()
    const coachHeadingRef = useRef<HTMLHeadingElement>(null)
    const teamHeadingRef = useRef<HTMLHeadingElement>(null)
    const addressHeadingRef = useRef<HTMLHeadingElement>(null)
    const tournamentsHeadingRef = useRef<HTMLHeadingElement>(null)
    const submitHeadingRef = useRef<HTMLHeadingElement>(null)
    const paymentHeadingRef = useRef<HTMLHeadingElement>(null)
    const fetchedTournamentsForTeamId = useRef<string | null>(null)

    /**
     * Editing a Coach field can change who — if anyone — the form matches, so
     * everything the previous match produced is thrown away, including any
     * code already in flight. Back at `idle`, re-submitting Coach can offer
     * the prefill again from scratch (ENG-4059).
     */
    const resetVerification = (): void => {
        setVerificationStatus('idle')
        setVerificationChannel(null)
        setVerificationErrors({})
        setResendLockedUntil(null)
        setValues((previous) => ({...previous, verification_code: ''}))
    }

    const setValue = (key: string, value: FieldValue): void => {
        setValues((previous) => ({...previous, [key]: value}))

        // Editing an already-saved section instantly re-locks everything
        // after it (see the `xState` expressions below) — announce that
        // explicitly, since hiding already-completed steps is a bigger
        // context change than a normal field edit.
        if (key.startsWith('coach_') || key === 'tcpa_consent') {
            if (coachSaved) {
                setLiveAnnouncement(cascadeMessage('Coach', 0))
            }

            setCoachSaved(false)
            setTeamSaved(false)
            setAddressSaved(false)
            setOpenSectionOverride(null)
            resetVerification()
        } else if (key.startsWith('team_')) {
            if (teamSaved) {
                setLiveAnnouncement(cascadeMessage('Team', 1))
            }

            setTeamSaved(false)
            setAddressSaved(false)
            setOpenSectionOverride(null)
        } else if (key.startsWith('address_')) {
            if (addressSaved) {
                setLiveAnnouncement(cascadeMessage('Address', 2))
            }

            setAddressSaved(false)
            setOpenSectionOverride(null)
        }
    }

    const setTeamCategory = (key: string, value: FieldValue): void => {
        setValue(key, value)
        setValues((previous) => ({...previous, team_age_id: '', team_classification_id: ''}))
    }

    const stringValue = (key: string): string => {
        const value = values[key]
        return typeof value === 'string' ? value : ''
    }

    const tcpaConsentGiven = values.tcpa_consent === true

    // Named because `teamState` and the scroll effect below both hinge on it:
    // Team stays locked for exactly as long as the widget is waiting on the
    // coach.
    const verificationPromptVisible = verificationStatus !== 'idle'

    // Deliberately the name they typed, not the one on the matched Person:
    // the greeting renders before anyone has verified, so echoing what's on
    // file would tell whoever typed the email that account's real first name.
    const coachGreetingName = stringValue('coach_first_name').trim() || 'there'

    const coachFieldsComplete = ['coach_first_name', 'coach_last_name', 'coach_mobile', 'coach_email']
        .every((key) => stringValue(key) !== '')

    const goToTeamSection = (): void => {
        scrollToSection(teamHeadingRef)
    }

    const goToAddressSection = (): void => {
        scrollToSection(addressHeadingRef)
    }

    const goToTournamentsSection = (): void => {
        scrollToSection(tournamentsHeadingRef)
    }

    // Clears the announcement shortly after it's set so that two identical
    // ones in a row (e.g. edit Coach, re-save, edit Coach again; or decline
    // the prefill offer twice) each produce a real DOM text mutation —
    // otherwise the second, equally real, event would set the exact same
    // string and React would bail the update (`Object.is` equality on an
    // unchanged primitive), silently skipping the second announcement.
    useEffect(() => {
        if (liveAnnouncement === '') {
            return
        }

        const timeout = setTimeout(() => {
            setLiveAnnouncement('')
        }, 1000)

        return () => {
            clearTimeout(timeout)
        }
    }, [liveAnnouncement])

    // Scrolling post-commit, rather than calling goToTeamSection() from the
    // handlers that unlock Team, is what keeps the target accurate:
    // dismissing the widget unmounts it and (on a prefill) can reveal the
    // Age/Classification dropdowns, both of which move the Team heading.
    // Called inline, scrollIntoView would aim at the pre-shift position and
    // overshoot; here the layout has already committed. Same reason focus
    // never lands on the heading before `teamState` has actually flipped
    // from `locked` to `active` in the DOM.
    useEffect(() => {
        if (coachSaved && !verificationPromptVisible) {
            goToTeamSection()
        }
    }, [coachSaved, verificationPromptVisible])

    useEffect(() => {
        if (teamSaved) {
            goToAddressSection()
        }
    }, [teamSaved])

    useEffect(() => {
        if (addressSaved) {
            goToTournamentsSection()
        }
    }, [addressSaved])

    // Same reasoning as above, applied to the form<->payment screen swap:
    // both `<main>` blocks stay mounted (toggled via `hidden`), which drops
    // focus to `<body>` with no cue a screen changed. Skips the initial
    // mount so landing on the form doesn't steal focus from the page load.
    const isFirstScreenRender = useRef(true)

    useEffect(() => {
        if (isFirstScreenRender.current) {
            isFirstScreenRender.current = false

            return
        }

        scrollToSection(screen === 'payment' ? paymentHeadingRef : submitHeadingRef)
    }, [screen])

    // Pay Now/Pay Later swap `PaymentSection` straight from the summary (or
    // Rainforest widget) to the confirmation without `screen` itself
    // changing — both happen while already on `screen === 'payment'` — so
    // the effect above never fires for this transition. Left uncovered, the
    // confirmation is a fresh DOM insertion with nothing pointing focus at
    // it, and a just-approved Rainforest widget (often an iframe) unmounts
    // at the same moment, dropping any focus that was inside it to `<body>`.
    const isFirstStatusRender = useRef(true)

    useEffect(() => {
        if (isFirstStatusRender.current) {
            isFirstStatusRender.current = false

            return
        }

        if (status === 'submitted') {
            scrollToSection(paymentHeadingRef)
        }
    }, [status])

    // Fetches once per Team, the first time its Address has been submitted —
    // the Team's declared Age/Classification (not local form state) is the
    // source of truth the server uses to compute eligibility, and it can't
    // change from here on, so a later Address resubmission (addressSaved
    // flipping again) shouldn't re-fetch. Only a genuine failure re-arms the
    // fetch, via `fetchedTournamentsForTeamId` staying unset until it succeeds.
    useEffect(() => {
        if (teamId == null || !addressSaved || fetchedTournamentsForTeamId.current === teamId) {
            return
        }

        let cancelled = false
        setTournamentsLoading(true)
        setTournamentsError(null)

        axios.get<{tournaments: Tournament[]}>(
            route('tournament-registrations.public.tournaments.index', {registrationForm: form.id}),
            {params: {team_id: teamId}},
        )
            .then((response) => {
                if (!cancelled) {
                    setTournaments(response.data.tournaments)
                    fetchedTournamentsForTeamId.current = teamId
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setTournamentsError('Unable to load tournaments. Please refresh and try again.')
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setTournamentsLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [teamId, addressSaved, form.id])

    const submitCoach = async (): Promise<void> => {
        setCoachSaving(true)
        setCoachErrors({})

        try {
            const response = await axios.post<{matched: boolean, person_id: string}>(
                route('tournament-registrations.public.coach.store', {registrationForm: form.id}),
                {
                    coach_first_name: stringValue('coach_first_name'),
                    coach_last_name: stringValue('coach_last_name'),
                    coach_mobile: stringValue('coach_mobile'),
                    coach_email: stringValue('coach_email'),
                    tcpa_consent: tcpaConsentGiven,
                },
            )
            setCoachSaved(true)
            setCoachPersonId(response.data.person_id)
            setOpenSectionOverride(null)

            if (response.data.matched) {
                setVerificationStatus('offered')
            }
        } catch (e) {
            const errors = firstValidationErrors(e)

            if (errors != null) {
                setCoachErrors(errors)
            }
        } finally {
            setCoachSaving(false)
        }
    }

    const sendVerificationCode = async (): Promise<void> => {
        if (coachPersonId == null) {
            return
        }

        setVerificationSending(true)
        setVerificationErrors({})

        try {
            const response = await axios.post<{sent: boolean, channel: 'sms' | 'email'}>(
                route('tournament-registrations.public.verification.send', {registrationForm: form.id}),
                {person_id: coachPersonId},
            )
            setVerificationChannel(response.data.channel)
            setVerificationStatus('sent')
            setResendLockedUntil(Date.now() + RESEND_COOLDOWN_MS)
        } catch (e) {
            // The widget only renders `errors.verification`, but a failed send
            // rarely arrives under that key: a throttled request (429) carries
            // a bare `message`, which `firstValidationErrors` files under
            // `submission`, and "no usable channel" is a 422 on `person_id`.
            // Left unmapped, both left the offer on screen with Yes/No still
            // showing and no indication that pressing Yes had done anything.
            const errors = firstValidationErrors(e) ?? {}
            const message = errors.verification ?? errors.submission ?? errors.person_id

            setVerificationErrors({
                verification: message ?? 'We could not send your code. Please try again.',
            })
        } finally {
            setVerificationSending(false)
        }
    }

    const submitVerificationCode = async (): Promise<void> => {
        if (coachPersonId == null) {
            return
        }

        setVerificationChecking(true)
        setVerificationErrors({})

        try {
            // The typed coach details ride along so a good code can write
            // them onto the matched Person (ENG-4259) — the Coach step
            // deliberately doesn't, since nothing is verified there.
            const response = await axios.post<VerificationCheckResponse>(
                route('tournament-registrations.public.verification.check', {registrationForm: form.id}),
                {
                    person_id: coachPersonId,
                    code: stringValue('verification_code'),
                    coach_first_name: stringValue('coach_first_name'),
                    coach_last_name: stringValue('coach_last_name'),
                    coach_mobile: stringValue('coach_mobile'),
                    coach_email: stringValue('coach_email'),
                },
            )

            const {team, address} = response.data

            // The coach already said Yes on the offer screen, so a good code
            // is the last gate — apply what came back and get out of the way.
            setValues((previous) => applyPrefill(previous, team, address, team_categories))
            setPrefillVersion((previous) => previous + 1)
            setVerificationStatus('idle')
            setLiveAnnouncement(team == null && address == null ? MANUAL_ENTRY_ANNOUNCEMENT : PREFILLED_ANNOUNCEMENT)
        } catch (e) {
            const errors = firstValidationErrors(e)

            if (errors != null) {
                setVerificationErrors(errors)
            }
        } finally {
            setVerificationChecking(false)
        }
    }

    /**
     * Shared by "No" on the prefill offer and the "Skip and enter manually"
     * escape hatch on the code step behind it — both dismiss the widget and
     * leave the form to be filled in by hand, same as an unmatched coach.
     */
    const continueWithoutPrefill = (): void => {
        setVerificationStatus('idle')
        setLiveAnnouncement(MANUAL_ENTRY_ANNOUNCEMENT)
    }

    const submitTeam = async (): Promise<void> => {
        if (coachPersonId == null) {
            return
        }

        setTeamSaving(true)
        setTeamErrors({})

        try {
            const response = await axios.post<{matched: boolean, team_id: string}>(
                route('tournament-registrations.public.team.store', {registrationForm: form.id}),
                {
                    coach_person_id: coachPersonId,
                    team_name: stringValue('team_name'),
                    team_category_id: stringValue('team_category_id') || null,
                    team_age_id: stringValue('team_age_id') || null,
                    team_classification_id: resolvedTeamClassificationId || null,
                },
            )
            setTeamSaved(true)
            setTeamId(response.data.team_id)
            setOpenSectionOverride(null)
        } catch (e) {
            const errors = firstValidationErrors(e)

            if (errors != null) {
                setTeamErrors(errors)
            }
        } finally {
            setTeamSaving(false)
        }
    }

    const submitAddress = async (): Promise<void> => {
        if (teamId == null) {
            return
        }

        setAddressSaving(true)
        setAddressErrors({})

        try {
            await axios.post<{address_id: string}>(
                route('tournament-registrations.public.address.store', {registrationForm: form.id}),
                {
                    team_id: teamId,
                    address_street_1: stringValue('address_street_1'),
                    address_street_2: stringValue('address_street_2') || null,
                    address_locality: stringValue('address_locality'),
                    address_region: stringValue('address_region'),
                    address_postal_code: stringValue('address_postal_code'),
                    address_country: stringValue('address_country') || null,
                },
            )
            setAddressSaved(true)
            setOpenSectionOverride(null)
        } catch (e) {
            const errors = firstValidationErrors(e)

            if (errors != null) {
                setAddressErrors(errors)
            }
        } finally {
            setAddressSaving(false)
        }
    }

    const selectedTournamentIds = Object.keys(picks)

    // Shared by the submission payload and classificationsComplete below —
    // both need the same tournament/pick lookup to resolve a division's
    // classification id, so this is the one place that pairs them.
    const classificationIdForTournament = (tournamentId: string): string | null => {
        const tournament = tournaments.find((candidate) => candidate.id === tournamentId)
        const pick = picks[tournamentId]

        return resolvedClassificationId(tournament, resolvedAgeId(tournament, pick), pick)
    }

    // Submits the form; a submission with no paid tournaments (pure Pay
    // Later) is done at this point. One with paid tournaments comes back
    // with a paymentComponent descriptor instead of finishing — the
    // coach still has to enter a card in the widget PaymentSection mounts,
    // which finalizes via finalizePayment() below on approval.
    const submitRegistration = async (choice: PayChoice): Promise<void> => {
        if (coachPersonId == null || teamId == null) {
            return
        }

        // Pay Later is a cancel-the-payment-flow action, not "pay for
        // whatever's still checked" — it must defer every selected
        // tournament regardless of per-tournament checkbox state (ENG-3923).
        const paidTournamentIdsToSubmit = choice === 'later' ? [] : paidTournamentIds

        setStatus('submitting')
        setSubmitErrors({})

        try {
            const response = await axios.post<{submission_id: string, paymentComponent?: PaymentComponentDescriptor}>(
                route('tournament-registrations.public.submissions.store', {registrationForm: form.id}),
                {
                    coach_person_id: coachPersonId,
                    team_id: teamId,
                    coach_email: stringValue('coach_email'),
                    coach_mobile: stringValue('coach_mobile'),
                    tcpa_consent: tcpaConsentGiven,
                    captcha_token: turnstile.token,
                    tournament_ids: selectedTournamentIds,
                    tournament_age_ids: Object.fromEntries(
                        selectedTournamentIds
                            .map((tournamentId) => {
                                const tournament = tournaments.find((candidate) => candidate.id === tournamentId)

                                return [tournamentId, resolvedAgeId(tournament, picks[tournamentId])] as const
                            })
                            .filter((entry): entry is [string, string] => entry[1] != null),
                    ),
                    tournament_classification_ids: Object.fromEntries(
                        selectedTournamentIds
                            .map((tournamentId) => [tournamentId, classificationIdForTournament(tournamentId)] as const)
                            .filter((entry): entry is [string, string] => Boolean(entry[1])),
                    ),
                    pay_choice: choice,
                    paid_tournament_ids: paidTournamentIdsToSubmit,
                    custom_fields: Object.fromEntries(
                        fields
                            .map((field) => [field.id, values[`custom_${field.id}`]] as const)
                            .filter(([, value]) => value !== undefined),
                    ),
                },
            )

            setSubmissionId(response.data.submission_id)

            if (choice === 'later') {
                setPaidTournamentIds([])
            }

            const descriptor = response.data.paymentComponent

            if (descriptor != null) {
                setPaymentConfig(descriptor)
                setStatus('idle')
            } else {
                setStatus('submitted')
            }
        } catch (e) {
            const errors = firstValidationErrors(e)

            if (errors != null) {
                setSubmitErrors(errors)

                if (Object.keys(errors).some((key) => key.startsWith('custom_fields.'))) {
                    setScreen('form')
                }
            }

            turnstile.reset()
            setStatus('idle')
        }
    }

    // Fired once the payment widget reports an approved charge; confirms
    // it server-side and allocates it across the paid tournaments.
    const finalizePayment = async (detail: ApprovedPaymentDetail): Promise<void> => {
        if (submissionId == null) {
            return
        }

        setStatus('submitting')
        setSubmitErrors({})

        try {
            await axios.post(
                route('tournament-registrations.public.submissions.payment.store', {
                    registrationForm: form.id,
                    registrationFormSubmission: submissionId,
                }),
                {
                    transaction_id: detail.chargeId,
                    payment_method_id: detail.paymentMethodId,
                },
            )
            setStatus('submitted')
        } catch (e) {
            const errors = firstValidationErrors(e)

            if (errors != null) {
                setSubmitErrors(errors)
            }

            setStatus('idle')
        }
    }

    const toggleTournament = (tournamentId: string): void => {
        const tournament = tournaments.find((candidate) => candidate.id === tournamentId)

        if (tournament != null && isAlreadyPaid(tournament)) {
            return
        }

        // Unpaid registrations open payment for this tournament (ENG-4346).
        if (tournament != null && isUnpaidRegistration(tournament)) {
            setTournamentsError(null)
            setPayPromptTournament(tournament)

            return
        }

        setPicks((previous) => {
            if (previous[tournamentId] != null) {
                const next = {...previous}
                delete next[tournamentId]

                return next
            }

            return {
                ...previous,
                [tournamentId]: {
                    ageId: tournament?.default_age_id ?? '',
                    classificationId: null,
                },
            }
        })
    }

    const confirmLookingToPay = async (): Promise<void> => {
        if (payPromptTournament == null || teamId == null) {
            return
        }

        setPayPromptOpening(true)
        setTournamentsError(null)

        try {
            const response = await axios.post<{url: string}>(
                route('tournament-registrations.public.tournaments.payment-link', {
                    registrationForm: form.id,
                    event: payPromptTournament.id,
                }),
                {team_id: teamId},
            )
            window.location.assign(response.data.url)
        } catch (e) {
            const errors = firstValidationErrors(e) ?? {}

            setTournamentsError(
                errors.submission
                ?? errors.team_id
                ?? 'We could not open payment for this tournament. Please contact the organizer.',
            )
            setPayPromptTournament(null)
            setPayPromptOpening(false)
        }
    }

    const togglePaidTournament = (tournamentId: string): void => {
        setPaidTournamentIds((previous) => previous.includes(tournamentId)
            ? previous.filter((id) => id !== tournamentId)
            : [...previous, tournamentId])
    }

    // Defaults to "pay for everything selected" each time the coach arrives
    // at the Payment screen, so edits made after going Back (adding/removing
    // a tournament) aren't left reflecting a stale checkbox state.
    const goToPayment = (): void => {
        setPaidTournamentIds(selectedTournamentIds)
        setScreen('payment')
    }

    const goBackToForm = (): void => {
        setPaymentConfig(null)
        setSubmissionId(null)
        setScreen('form')
    }

    // Empty only when no Category has a tournament open for registration —
    // the server already filtered them out (ENG-4060), so there is nothing
    // for the coach to pick and the whole Team block collapses to a message.
    const hasSelectableSports = team_categories.length > 0

    const teamCategoryOptions = useMemo(
        () => toOptions(team_categories),
        [team_categories],
    )

    const selectedTeamCategory = useMemo(
        () => team_categories.find((category) => category.id === stringValue('team_category_id')) ?? null,
        [team_categories, values.team_category_id],
    )

    const teamAgeOptions = useMemo(
        () => toOptions(selectedTeamCategory?.ages ?? []),
        [selectedTeamCategory],
    )

    const teamClassificationOptions = useMemo(
        () => toOptions(selectedTeamCategory?.classifications ?? []),
        [selectedTeamCategory],
    )

    // A Category with a single Classification (e.g. an HS-only sport with no
    // competitive tiers) has nothing for the coach to pick — same reasoning
    // as the single-Category Sport preselect above, and mirroring the sole-
    // classification auto-select ENG-4260 shipped for per-tournament division
    // picks. Gated on Age (ENG-4279) so Continue cannot fire before the
    // team's Age is declared. Resolved at read time, like
    // `resolvedClassificationId` there, rather than written into `values`.
    const selectedTeamAgeId = stringValue('team_age_id')
    const soleTeamClassificationId = soleClassificationForTeamAge(selectedTeamCategory, selectedTeamAgeId)
    const resolvedTeamClassificationId = resolveTeamClassificationId(
        selectedTeamCategory,
        selectedTeamAgeId,
        stringValue('team_classification_id'),
    )

    const selectedTeamAgeName = selectedTeamCategory?.ages
        .find((age) => age.id === selectedTeamAgeId)?.name ?? null

    const selectedTeamClassificationName = selectedTeamCategory?.classifications
        .find((classification) => classification.id === resolvedTeamClassificationId)?.name ?? null

    const usaStateOptions = useMemo(
        () => usaStates.map((state) => new KeyValuePair(state.abbreviation, state.name)),
        [],
    )

    const teamFieldsComplete = teamStepFieldsComplete({
        coachPersonId,
        teamName: stringValue('team_name'),
        categoryId: stringValue('team_category_id'),
        ageId: selectedTeamAgeId,
        classificationId: stringValue('team_classification_id'),
        category: selectedTeamCategory,
    })

    const addressFieldsComplete = teamId != null
        && stringValue('address_street_1') !== ''
        && stringValue('address_locality') !== ''
        && stringValue('address_region') !== ''
        && stringValue('address_postal_code') !== ''

    const customFieldsAreComplete = customFieldsComplete(fields, values)

    // Each section's state is computed here, rather than inferred inside
    // `Section`, since the six sections' "done" signals are heterogeneous —
    // three are POST-confirmed (`coachSaved`/`teamSaved`/`addressSaved`), two
    // are pure local-state (`picks`/custom fields), and
    // Submit's gate is a combination of the rest. Tournament Selection and
    // Additional Questions intentionally never reach `completed` — both are
    // open-ended (a multi-select, free-form answers) with no discrete "done"
    // action, so collapsing them the instant their minimum is met would hide
    // the rest of the section mid-interaction.
    const coachState: SectionState = openSectionOverride === 'coach'
        ? 'active'
        : !coachSaved ? 'active' : 'completed'

    const teamState: SectionState = openSectionOverride === 'team'
        ? 'active'
        : !coachSaved || verificationPromptVisible
            ? 'locked'
            : !teamSaved ? 'active' : 'completed'

    const addressState: SectionState = openSectionOverride === 'address'
        ? 'active'
        : !teamSaved
            ? 'locked'
            : !addressSaved ? 'active' : 'completed'

    const tournamentsState: SectionState = !addressSaved ? 'locked' : 'active'

    // Additional Questions and Submit share the same gate (reaching Tournament
    // Selection with at least one pick) — an incomplete required custom field
    // disables the Continue button itself (unchanged, below) rather than
    // hiding the whole Submit section, so the coach can see what's left.
    const readyForFinalSteps = addressSaved && selectedTournamentIds.length > 0
    const customFieldsState: SectionState = readyForFinalSteps ? 'active' : 'locked'
    const submitState: SectionState = readyForFinalSteps ? 'active' : 'locked'

    // "Step X of Y" progress, derived from how many sections are unlocked so
    // far. Tournament Selection, Additional Questions, and Submit unlock
    // together (see `readyForFinalSteps` above) rather than completing
    // sequentially, so this jumps straight from Address to the final step
    // once a tournament is picked — there's no in-between step to linger on.
    const stepStates: SectionState[] = fields.length > 0
        ? [coachState, teamState, addressState, tournamentsState, customFieldsState, submitState]
        : [coachState, teamState, addressState, tournamentsState, submitState]
    const totalSteps = stepStates.length
    const currentStep = stepStates.filter((state) => state !== 'locked').length

    const editSection = (section: SectionKey, headingRef: React.RefObject<HTMLHeadingElement | null>): void => {
        setOpenSectionOverride(section)
        scrollToSection(headingRef)
    }

    const customFieldErrors = useMemo(
        () => Object.fromEntries(
            Object.entries(submitErrors)
                .filter(([key]) => key.startsWith('custom_fields.'))
                .map(([key, message]) => [key.slice('custom_fields.'.length), message]),
        ),
        [submitErrors],
    )

    // `tournaments` overridden per-tournament with whichever eligible
    // division is currently selected (registered_count/capacity/entry_fee),
    // so a play-up choice is reflected everywhere those fields are read —
    // the selection list itself, and the payment summary/totals below —
    // without needing a server round trip per selection change.
    const displayTournaments = useMemo(
        () => tournaments.map((tournament) => {
            const selectedOption = tournament.available_ages.find((option) => option.age_id === resolvedAgeId(tournament, picks[tournament.id]))

            return selectedOption == null ? tournament : {
                ...tournament,
                registered_count: selectedOption.registered_count,
                capacity: selectedOption.capacity,
                entry_fee: selectedOption.entry_fee,
            }
        }),
        [tournaments, picks],
    )

    const selectedTournaments = useMemo(
        () => displayTournaments.filter((tournament) => selectedTournamentIds.includes(tournament.id)),
        [displayTournaments, selectedTournamentIds],
    )

    const classificationsComplete = selectedTournamentIds.every((tournamentId) => Boolean(classificationIdForTournament(tournamentId)))

    const paidTournaments = useMemo(
        () => selectedTournaments.filter((tournament) => paidTournamentIds.includes(tournament.id)),
        [selectedTournaments, paidTournamentIds],
    )

    const deferredTournaments = useMemo(
        () => selectedTournaments.filter((tournament) => !paidTournamentIds.includes(tournament.id)),
        [selectedTournaments, paidTournamentIds],
    )

    const dueNowCents = paidTournaments.reduce((total, tournament) => total + (tournament.entry_fee ?? 0), 0)
    const deferredCents = deferredTournaments.reduce((total, tournament) => total + (tournament.entry_fee ?? 0), 0)

    return <>
        <Head title={form.name}/>

        <header data-sentry-mask className={'sticky top-0 z-10 flex flex-col border-b border-gray-200 bg-white px-5 py-[14px]'}>
            <div className={'mx-auto flex w-full max-w-2xl items-center gap-3'}>
                {logo_url != null && logo_url !== '' ? (
                    <img
                        src={logo_url}
                        alt={`${organization_name} logo`}
                        data-testid={'registration-form-logo'}
                        className={'h-10 w-10 shrink-0 object-contain'}
                    />
                ) : (
                    <Logo size={40}/>
                )}
                <div
                    className={'min-w-0 text-xl font-semibold text-gray-900'}
                    data-testid={'registration-form-name'}
                >
                    {form.name}
                </div>
            </div>
            {screen === 'form' && (
                <div
                    className={'mx-auto mt-3 flex w-full max-w-2xl flex-col gap-1.5'}
                    data-testid={'registration-step-indicator'}
                >
                    <div className={'flex items-center justify-between text-[14px] font-medium text-gray-500'}>
                        <span>Registration</span>
                        <span data-testid={'registration-step-indicator-label'}>{`Step ${currentStep} of ${totalSteps}`}</span>
                    </div>
                    <div
                        role={'progressbar'}
                        aria-label={'Registration progress'}
                        aria-valuenow={currentStep}
                        aria-valuemin={1}
                        aria-valuemax={totalSteps}
                        aria-valuetext={`Step ${currentStep} of ${totalSteps}`}
                        className={'h-1 w-full overflow-hidden rounded-full bg-gray-200'}
                    >
                        <div
                            className={'h-full rounded-full bg-royal-10 transition-all duration-300 ease-in-out'}
                            style={{width: `${(currentStep / totalSteps) * 100}%`}}
                        />
                    </div>
                </div>
            )}
        </header>

        <span role={'status'} aria-live={'polite'} className={'sr-only'}>{liveAnnouncement}</span>

        <main data-sentry-mask className={classNames(MAIN_CLASS, screen !== 'form' ? 'hidden' : '')}>
            <Section
                title={'Coach'}
                icon={<UserIcon aria-hidden={'true'} className={'size-3.5'}/>}
                testId={'registration-section-coach'}
                state={coachState}
                headingRef={coachHeadingRef}
                summary={coachSummary({
                    coach_first_name: stringValue('coach_first_name'),
                    coach_last_name: stringValue('coach_last_name'),
                    coach_email: stringValue('coach_email'),
                })}
                onEdit={() => editSection('coach', coachHeadingRef)}
                liveMessage={coachSaving ? 'Saving…' : coachSaved ? 'Coach details saved.' : ''}
            >
                <Input
                    type={'text'}
                    title={'First Name'}
                    field={'coach_first_name'}
                    value={stringValue('coach_first_name')}
                    setData={setValue}
                    error={coachErrors.coach_first_name}
                    testId={'registration-coach-first-name'}
                />
                <Input
                    type={'text'}
                    title={'Last Name'}
                    field={'coach_last_name'}
                    value={stringValue('coach_last_name')}
                    setData={setValue}
                    error={coachErrors.coach_last_name}
                    testId={'registration-coach-last-name'}
                />
                <div className={'block relative'}>
                    <label className={'form-control-label'} htmlFor={'coach_mobile'}>Mobile</label>
                    <PhoneNumberInput
                        id={'coach_mobile'}
                        data-testid={'registration-coach-mobile'}
                        className={classNames('form-control', coachErrors.coach_mobile ? 'has-error' : '')}
                        value={stringValue('coach_mobile')}
                        onChange={(value) => {
                            setValue('coach_mobile', value)
                        }}
                    />
                    <InputErrorLabel error={coachErrors.coach_mobile} testId={'registration-coach-mobile-error'}/>
                </div>
                <Input
                    type={'email'}
                    title={'Email'}
                    field={'coach_email'}
                    value={stringValue('coach_email')}
                    setData={setValue}
                    error={coachErrors.coach_email}
                    testId={'registration-coach-email'}
                />
                <SidebarToggle
                    id={'registration-tcpa-consent'}
                    justify={'items-start'}
                    title={<span>
                        {`I agree to receive text messages related to my relationship with ${organization_name}. Message and data rates may apply. Reply STOP to opt out.`}
                        <span className={'sr-only'}> (required)</span>
                    </span>}
                    checked={tcpaConsentGiven}
                    error={coachErrors.tcpa_consent}
                    onChange={(checked) => {
                        setValue('tcpa_consent', checked)
                    }}
                />
                <RegistrationButton
                    size={'lg'}
                    classes={['w-full cursor-pointer']}
                    disabled={!coachFieldsComplete || !tcpaConsentGiven || coachSaving}
                    onClick={submitCoach}
                    testId={'registration-coach-continue'}
                >
                    {coachSaving ? 'Saving…' : coachSaved ? 'Saved' : 'Continue'}
                </RegistrationButton>
                <TermsDisclosure
                    className={''}
                    termsUrl={'https://duesy.me/go/terms.user.reg'}
                    privacyUrl={'https://duesy.me/go/privacy.reg'}
                />
            </Section>

            {verificationStatus !== 'idle' && (
                <VerificationPrompt
                    status={verificationStatus}
                    channel={verificationChannel}
                    firstName={coachGreetingName}
                    code={stringValue('verification_code')}
                    setCode={(value) => {
                        setValue('verification_code', value)
                    }}
                    errors={verificationErrors}
                    sending={verificationSending}
                    checking={verificationChecking}
                    resendLockedUntil={resendLockedUntil}
                    onSend={sendVerificationCode}
                    onVerify={submitVerificationCode}
                    onContinueWithoutPrefill={continueWithoutPrefill}
                />
            )}

            <Section
                title={'Team'}
                icon={<UsersIcon aria-hidden={'true'} className={'size-3.5'}/>}
                testId={'registration-section-team'}
                state={teamState}
                headingRef={teamHeadingRef}
                summary={teamSummary(stringValue('team_name'), selectedTeamCategory?.name ?? null, selectedTeamAgeName, selectedTeamClassificationName)}
                onEdit={() => editSection('team', teamHeadingRef)}
                liveMessage={teamSaving ? 'Saving…' : teamSaved ? 'Team details saved.' : ''}
            >
                <Input
                    key={`team-name-${prefillVersion}`}
                    type={'text'}
                    title={'Team Name'}
                    description={field_descriptions.team_name}
                    field={'team_name'}
                    value={stringValue('team_name')}
                    setData={setValue}
                    error={teamErrors.team_name}
                    testId={'registration-team-name'}
                />
                {!hasSelectableSports ? (
                    <div
                        data-testid={'registration-team-no-sports'}
                        className={'rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500'}
                    >
                        {organization_name} has no tournaments open for registration right now. Please check back later.
                    </div>
                ) : (
                    <>
                        <SelectDropdown
                            key={`team-category-${prefillVersion}`}
                            type={'native'}
                            title={'Sport'}
                            description={field_descriptions.sport}
                            id={'team_category_id'}
                            options={teamCategoryOptions}
                            value={stringValue('team_category_id')}
                            setData={setTeamCategory}
                            field={'team_category_id'}
                            error={teamErrors.team_category_id}
                            testId={'registration-team-category'}
                        />
                        {selectedTeamCategory != null ? (
                            <>
                                <SelectDropdown
                                    key={`team-age-${prefillVersion}-${selectedTeamCategory.id}`}
                                    type={'native'}
                                    title={'Age'}
                                    description={field_descriptions.age}
                                    id={'team_age_id'}
                                    options={teamAgeOptions}
                                    value={selectedTeamAgeId}
                                    setData={setValue}
                                    field={'team_age_id'}
                                    error={teamErrors.team_age_id}
                                    testId={'registration-team-age'}
                                />
                                {selectedTeamAgeId !== '' && selectedTeamCategory.classifications.length > 0 && (
                                    <>
                                        <SelectDropdown
                                            key={`team-classification-${prefillVersion}-${selectedTeamCategory.id}-${selectedTeamAgeId}`}
                                            type={'native'}
                                            title={'Classification'}
                                            description={field_descriptions.classification}
                                            id={'team_classification_id'}
                                            options={teamClassificationOptions}
                                            value={resolvedTeamClassificationId}
                                            setData={setValue}
                                            field={'team_classification_id'}
                                            error={teamErrors.team_classification_id}
                                            testId={'registration-team-classification'}
                                            disabled={soleTeamClassificationId != null}
                                        />
                                        {soleTeamClassificationId != null && (
                                            <span
                                                role={'status'}
                                                aria-live={'polite'}
                                                data-testid={'registration-team-classification-auto-announcement'}
                                                className={'sr-only'}
                                            >
                                                {`Classification selected automatically: ${selectedTeamClassificationName}.`}
                                            </span>
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            <p
                                data-testid={'registration-team-age-classification-hint'}
                                className={'text-[14px] text-gray-500'}
                            >
                                Select a sport to choose an age and classification.
                            </p>
                        )}
                        <RegistrationButton
                            size={'lg'}
                            classes={['w-full cursor-pointer']}
                            disabled={!teamFieldsComplete || teamSaving}
                            onClick={submitTeam}
                            testId={'registration-team-continue'}
                        >
                            {teamSaving ? 'Saving…' : teamSaved ? 'Saved' : 'Continue'}
                        </RegistrationButton>
                    </>
                )}
            </Section>

            <Section
                title={'Team Address'}
                icon={<MapPinIcon aria-hidden={'true'} className={'size-3.5'}/>}
                testId={'registration-section-address'}
                state={addressState}
                headingRef={addressHeadingRef}
                summary={addressSummary({
                    address_street_1: stringValue('address_street_1'),
                    address_locality: stringValue('address_locality'),
                    address_region: stringValue('address_region'),
                })}
                onEdit={() => editSection('address', addressHeadingRef)}
                liveMessage={addressSaving ? 'Saving…' : addressSaved ? 'Address details saved.' : ''}
            >
                <Input
                    key={`address-street-1-${prefillVersion}`}
                    type={'text'}
                    title={'Street Address'}
                    field={'address_street_1'}
                    value={stringValue('address_street_1')}
                    setData={setValue}
                    error={addressErrors.address_street_1}
                    testId={'registration-address-street-1'}
                />
                <Input
                    key={`address-street-2-${prefillVersion}`}
                    type={'text'}
                    title={'Street Address 2'}
                    field={'address_street_2'}
                    value={stringValue('address_street_2')}
                    setData={setValue}
                    error={addressErrors.address_street_2}
                    testId={'registration-address-street-2'}
                />
                <Input
                    key={`address-locality-${prefillVersion}`}
                    type={'text'}
                    title={'City'}
                    field={'address_locality'}
                    value={stringValue('address_locality')}
                    setData={setValue}
                    error={addressErrors.address_locality}
                    testId={'registration-address-locality'}
                />
                <SelectDropdown
                    key={`address-region-${prefillVersion}`}
                    type={'native'}
                    title={'State'}
                    id={'address_region'}
                    options={usaStateOptions}
                    value={stringValue('address_region')}
                    setData={setValue}
                    field={'address_region'}
                    error={addressErrors.address_region}
                    testId={'registration-address-region'}
                />
                <Input
                    key={`address-postal-code-${prefillVersion}`}
                    type={'text'}
                    title={'Postal Code'}
                    field={'address_postal_code'}
                    value={stringValue('address_postal_code')}
                    setData={setValue}
                    error={addressErrors.address_postal_code}
                    testId={'registration-address-postal-code'}
                />
                <Input
                    key={`address-country-${prefillVersion}`}
                    type={'text'}
                    title={'Country'}
                    field={'address_country'}
                    value={stringValue('address_country')}
                    setData={setValue}
                    error={addressErrors.address_country}
                    testId={'registration-address-country'}
                />
                <RegistrationButton
                    size={'lg'}
                    classes={['w-full cursor-pointer']}
                    disabled={!addressFieldsComplete || addressSaving}
                    onClick={submitAddress}
                    testId={'registration-address-continue'}
                >
                    {addressSaving ? 'Saving…' : addressSaved ? 'Saved' : 'Continue'}
                </RegistrationButton>
            </Section>

            <Section
                title={'Tournament Selection'}
                icon={<TrophyIcon aria-hidden={'true'} className={'size-3.5'}/>}
                testId={'registration-section-tournaments'}
                state={tournamentsState}
                headingRef={tournamentsHeadingRef}
            >
                {tournamentsError != null && (
                    <InputErrorLabel error={tournamentsError} testId={'registration-tournaments-error'}/>
                )}
                {tournamentsLoading ? (
                    <p
                        data-testid={'registration-tournaments-loading'}
                        className={'text-[14px] text-gray-500'}
                    >
                        Loading tournaments…
                    </p>
                ) : displayTournaments.length > 0 ? (
                    <>
                        <fieldset className={'flex flex-col gap-2'}>
                            <legend className={'form-control-label'}>Tournaments</legend>
                            {displayTournaments.map((tournament) => {
                                const alreadyPaid = isAlreadyPaid(tournament)
                                const alreadyRegistered = isAlreadyRegistered(tournament)
                                const isSelected = picks[tournament.id] != null
                                const pick = picks[tournament.id]
                                const selectedAgeId = resolvedAgeId(tournament, pick)
                                const selectedClassificationName = resolvedClassificationName(tournament, selectedAgeId, pick)

                                return (
                                    <div
                                        key={tournament.id}
                                        className={classNames(
                                            // Same "selected bordered box" language as the age
                                            // pills below, so a picked tournament and a picked
                                            // division read as one system. Note this does NOT copy
                                            // Components/Forms/ToggleCard.tsx's `bg-royal-60/5`:
                                            // that is 5% of #f6f9fe, i.e. effectively transparent,
                                            // and on this page's white surface (RegistrationForm.css,
                                            // ENG-4017) it rendered *lighter* than the unselected
                                            // state. Selection is carried by the border instead.
                                            // Every state stays on plain `border` + a color swap so
                                            // nothing here compiles to `box-shadow` —
                                            // `transition-colors` covers border/background/outline
                                            // color but NOT box-shadow, so a `ring-*` accent would
                                            // snap while the rest faded, and it would also collide
                                            // with the focus ring below over `--tw-ring-*`.
                                            'rounded-lg border transition-colors',
                                            // The card is the hit area (the label spans it) but is
                                            // not itself focusable, and `OPTION_INPUT_CLASS`'s
                                            // `focus:ring-shamrock-50` paints nothing — a ring color
                                            // with no ring width is inert, and @tailwindcss/forms is
                                            // not installed — so without this the only indicator is
                                            // the UA outline on a 16px checkbox in the corner.
                                            // Scoped to `input` deliberately: a bare
                                            // `has-[:focus-visible]` also matches the age pills
                                            // nested below, double-painting a focus indicator on
                                            // both the pill and the whole card.
                                            'has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-royal-20 has-[input:focus-visible]:ring-offset-2',
                                            // Borders here clear WCAG 1.4.11's 3:1 because the
                                            // border is the only thing delimiting a clickable card
                                            // — the same reasoning, and the same storm-50 landing
                                            // spot, as this page's `.form-control` rule in
                                            // RegistrationForm.css (ENG-4017). storm-80 is 1.4:1 on
                                            // white; storm-50 is 3.7:1, royal-20 is 3.7:1.
                                            // Already-paid recedes via a muted fill rather than
                                            // `opacity-50`: the disabled-control exemption does not
                                            // cover the count, dates and badge the coach still has
                                            // to read, and 50% opacity drops those to ~2:1.
                                            alreadyPaid
                                                ? 'border-storm-50 bg-gray-50'
                                                : isSelected
                                                    ? 'border-royal-20 bg-royal-60'
                                                    : 'border-storm-50 bg-white hover:border-royal-20 hover:bg-storm-100',
                                        )}
                                    >
                                        <div className={'flex items-start gap-2 px-4 py-3 text-[14px] text-gray-700'}>
                                            <label
                                                className={classNames(
                                                    'flex min-w-0 flex-1 items-start gap-2',
                                                    alreadyPaid ? 'cursor-not-allowed' : 'cursor-pointer',
                                                )}
                                            >
                                                <input
                                                    type={'checkbox'}
                                                    name={'tournament_ids'}
                                                    value={tournament.id}
                                                    checked={isSelected}
                                                    disabled={alreadyPaid}
                                                    aria-haspopup={isUnpaidRegistration(tournament) ? 'dialog' : undefined}
                                                    onChange={() => {
                                                        toggleTournament(tournament.id)
                                                    }}
                                                    className={classNames(OPTION_INPUT_CLASS, 'mt-0.5')}
                                                    data-testid={`registration-tournament-${tournament.id}`}
                                                />
                                                <span
                                                    // `gap-1` matches the `mt-1` rhythm
                                                    // `TournamentConfirmationBullets` uses for these same
                                                    // two lines one screen later. `min-w-0` so a long
                                                    // unbreakable name wraps instead of pushing past the
                                                    // card edge — flex items default to `min-width: auto`.
                                                    className={'flex min-w-0 flex-1 flex-col gap-1'}
                                                    data-testid={`registration-tournament-details-${tournament.id}`}
                                                >
                                                    <span className={'flex min-w-0 flex-wrap items-center gap-2 break-words'}>
                                                        {tournament.name}
                                                        {!alreadyPaid && alreadyRegistered && (
                                                            <Badge theme={'royal'}>Already Registered</Badge>
                                                        )}
                                                    </span>
                                                    <span
                                                        className={'flex items-center gap-1.5 text-gray-500'}
                                                        data-testid={`registration-tournament-registered-${tournament.id}`}
                                                    >
                                                        <UsersIcon aria-hidden={'true'} className={'size-4 shrink-0'}/>
                                                        {formatRegisteredCount(tournament)}
                                                    </span>
                                                    <span
                                                        className={'flex items-center gap-1.5 text-gray-500'}
                                                        data-testid={`registration-tournament-dates-${tournament.id}`}
                                                    >
                                                        <CalendarIcon aria-hidden={'true'} className={'size-4 shrink-0'}/>
                                                        {formatShortDateRange(tournament.starts_at, tournament.ends_at)}
                                                    </span>
                                                </span>
                                            </label>
                                            {(alreadyPaid || (isSelected && selectedAgeId != null)) && (
                                                // Upper-right of the card, not folded into the name
                                                // row above — a coach scanning several tournaments
                                                // reads "what will this cost me" as its own column,
                                                // the same way it later reads as its own column on the
                                                // Payment screen below. Paid status wins over the
                                                // figure itself: once paid, the number that mattered
                                                // before is moot, so it replaces the Already Paid
                                                // badge that used to sit on the name row (ENG-4262).
                                                // Kept as a sibling of <label>, not nested inside it —
                                                // matching PaymentSection's identical fee below — so it
                                                // never joins the checkbox's accessible name and can't
                                                // collide with the sr-only live region a few lines down
                                                // that announces this same figure on an Age change.
                                                <span
                                                    // Plain weight/color, not bold/gray-900 — matches
                                                    // this same per-item fee elsewhere (Payment screen
                                                    // below, RegistrationPaymentLink.tsx); the bold
                                                    // treatment is reserved for the totals row only.
                                                    className={'shrink-0 whitespace-nowrap text-[14px] text-gray-700'}
                                                    data-testid={`registration-tournament-entry-fee-${tournament.id}`}
                                                >
                                                    {alreadyPaid ? (
                                                        <Badge theme={'neutral'}>Already Paid</Badge>
                                                    ) : (
                                                        tournament.entry_fee != null ? formatCurrency(tournament.entry_fee) : 'Fee not set'
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                        {isSelected && selectedAgeId != null && (
                                            // Inside the card, not below it, so the division picker
                                            // reads as part of the tournament you just picked. Kept
                                            // outside the <label> above — a click on an age pill
                                            // nested in the label would toggle the tournament off.
                                            <div className={'flex flex-col gap-4 border-t border-royal-40 px-4 py-3'}>
                                                {tournament.available_ages.length > 1 && (
                                                    <AgeGroupCardGroup
                                                        tournament={tournament}
                                                        selectedAgeId={selectedAgeId}
                                                        disabled={alreadyPaid}
                                                        onChange={(ageId) => {
                                                            setPicks((previous) => ({
                                                                ...previous,
                                                                [tournament.id]: {ageId, classificationId: null},
                                                            }))
                                                        }}
                                                    />
                                                )}
                                                <ClassificationCardGroup
                                                    tournament={tournament}
                                                    selectedAgeId={selectedAgeId}
                                                    selectedClassificationId={resolvedClassificationId(tournament, selectedAgeId, pick) ?? ''}
                                                    disabled={alreadyPaid}
                                                    onChange={(classificationId) => {
                                                        setPicks((previous) => {
                                                            const current = previous[tournament.id]

                                                            if (current == null) {
                                                                return previous
                                                            }

                                                            return {
                                                                ...previous,
                                                                [tournament.id]: {...current, classificationId},
                                                            }
                                                        })
                                                    }}
                                                />
                                                <span role={'status'} aria-live={'polite'} className={'sr-only'}>
                                                    {`${tournament.name}: ${formatRegisteredCount(tournament)}${tournament.entry_fee != null ? `, ${formatCurrency(tournament.entry_fee)}` : ''}${selectedClassificationName != null ? `, Classification: ${selectedClassificationName}` : ''}`}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </fieldset>
                        <p
                            role={'status'}
                            aria-live={'polite'}
                            aria-atomic={'true'}
                            data-testid={'registration-tournaments-selected-count'}
                            className={selectedTournamentIds.length === 0 ? 'sr-only' : 'text-[14px] text-gray-500'}
                        >
                            {selectedTournamentIds.length === 0
                                ? ''
                                : `${tournamentSelectionSummary(selectedTournamentIds.length)}. ${
                                    !classificationsComplete
                                        ? 'Select a classification for each tournament.'
                                        : fields.length > 0
                                            ? 'Additional Questions and Submit are now available.'
                                            : 'Submit is now available.'
                                }`}
                        </p>
                    </>
                ) : (
                    <div
                        data-testid={'registration-tournaments-empty'}
                        className={'rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500'}
                    >
                        No tournaments available yet.
                    </div>
                )}
            </Section>

            <LookingToPayDialog
                tournamentName={payPromptTournament?.name ?? ''}
                open={payPromptTournament != null}
                opening={payPromptOpening}
                onConfirm={() => {
                    void confirmLookingToPay()
                }}
                onDismiss={() => {
                    setPayPromptTournament(null)
                }}
            />

            {fields.length > 0 && (
                <Section
                    title={'Additional Questions'}
                    icon={<ClipboardDocumentListIcon aria-hidden={'true'} className={'size-3.5'}/>}
                    testId={'registration-section-custom-fields'}
                    state={customFieldsState}
                >
                    {fields.some((field) => field.validations?.required === true) && (
                        <p className={'text-[14px] text-gray-500'}>
                            Fields marked <span aria-hidden={'true'}>*</span><span className={'sr-only'}>(required)</span> are required.
                        </p>
                    )}
                    {fields.map((field) => (
                        <CustomField
                            key={field.id}
                            field={field}
                            value={values[`custom_${field.id}`]}
                            setValue={setValue}
                            error={customFieldErrors[field.id]}
                        />
                    ))}
                </Section>
            )}

            <Section
                title={'Submit'}
                icon={<ClipboardDocumentCheckIcon aria-hidden={'true'} className={'size-3.5'}/>}
                testId={'registration-section-submit'}
                state={submitState}
                headingRef={submitHeadingRef}
            >
                {!classificationsComplete && selectedTournamentIds.length > 0 && (
                    <p className={'text-[14px] text-gray-500'}>
                        Select a classification for each selected tournament to continue.
                    </p>
                )}
                <RegistrationButton
                    size={'lg'}
                    classes={['w-full cursor-pointer']}
                    disabled={!addressSaved || selectedTournamentIds.length === 0 || !classificationsComplete || !customFieldsAreComplete}
                    onClick={goToPayment}
                    testId={'registration-continue-to-payment'}
                >
                    Continue to Payment
                </RegistrationButton>
            </Section>
        </main>

        <main
            className={classNames(MAIN_CLASS, screen !== 'payment' ? 'hidden' : '')}
            data-testid={'registration-payment-screen'}
        >
            <PaymentSection
                submitting={status === 'submitting'}
                submitted={status === 'submitted'}
                headingRef={paymentHeadingRef}
                paymentConfig={paymentConfig}
                selectedTournaments={selectedTournaments}
                paidTournamentIds={paidTournamentIds}
                paidTournaments={paidTournaments}
                deferredTournaments={deferredTournaments}
                dueNowCents={dueNowCents}
                deferredCents={deferredCents}
                payLaterEnabled={pay_later_enabled}
                errors={submitErrors}
                turnstileContainerRef={turnstile.containerRef}
                turnstileToken={turnstile.token}
                onTogglePaidTournament={togglePaidTournament}
                onBack={goBackToForm}
                onPayNow={() => submitRegistration('now')}
                onPayLater={() => submitRegistration('later')}
                onApprovedPayment={finalizePayment}
            />
        </main>
    </>
}

/**
 * Renders one of two visible states so the form reads as a stepped/
 * checkout-style flow: `completed` sections collapse to a one-line summary
 * plus an Edit button that reopens them; `active` is today's fully-rendered,
 * fully-interactive card. `locked` sections render nothing at all — the step
 * indicator above communicates how many are left, so there's no need to show
 * upcoming headers. Whether a section is "done" is computed by the parent
 * (some sections are POST-confirmed, some are pure local-state) rather than
 * inferred here.
 */
function Section(
    {title, icon, testId, state, headingRef, summary, onEdit, liveMessage, children}: {
        title: string
        icon: ReactNode
        testId: string
        state: SectionState
        headingRef?: React.RefObject<HTMLHeadingElement | null>
        summary?: ReactNode
        onEdit?: () => void
        liveMessage?: string
        children: ReactNode
    },
): ReactNode {
    const headingId = useId()

    if (state === 'locked') {
        return null
    }

    return <section
        data-testid={testId}
        aria-labelledby={headingId}
        className={'flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6'}
    >
        <div className={'flex items-center gap-2'}>
            {state === 'completed'
                ? <CheckCircleIcon aria-hidden={'true'} className={'size-5 shrink-0 text-shamrock-50'}/>
                : <span className={'section-icon'} aria-hidden={'true'}>{icon}</span>}
            <h2
                id={headingId}
                ref={headingRef}
                tabIndex={-1}
                className={'scroll-mt-28 rounded text-base font-semibold text-gray-800 focus-visible:ring-2 focus-visible:ring-royal-20 focus-visible:ring-offset-2 focus-visible:outline-none'}
            >
                {title}
            </h2>
        </div>
        {state === 'completed' && (
            <>
                <p data-testid={`${testId}-summary`} className={'text-[14px] text-gray-600'}>
                    {summary}
                </p>
                <button
                    type={'button'}
                    data-testid={`${testId}-edit`}
                    aria-label={`Edit ${title}`}
                    onClick={onEdit}
                    className={'-mx-2 -my-2.5 self-start rounded px-2 py-2.5 text-[14px] font-medium text-gray-500 underline focus-visible:ring-2 focus-visible:ring-royal-20 focus-visible:ring-offset-2 focus-visible:outline-none'}
                >
                    Edit
                </button>
            </>
        )}
        {liveMessage != null && (
            <span role={'status'} aria-live={'polite'} className={'sr-only'}>{liveMessage}</span>
        )}
        {state === 'active' && children}
    </section>
}

/**
 * Summary + Pay Now/Pay Later shown once the coach clicks "Continue to
 * Payment." Nothing here is persisted until Pay Now/Pay Later is clicked, so
 * Back is a plain screen toggle in the parent — no request to undo. The
 * per-tournament checkbox only renders when the org's Pay Later flag is on;
 * otherwise every selected tournament is paid in full, matching the org
 * setting "the coach must pay in order to complete their registration." That
 * checkbox only affects Pay Now (letting it pay a subset now and defer the
 * rest) — Pay Later ignores it entirely and always defers every selected
 * tournament, acting like a cancel of the payment step (ENG-3923).
 *
 * Once Pay Now returns a payment component descriptor (i.e. `paymentConfig`
 * is set — only happens when one or more tournaments are being paid now),
 * the summary/buttons are replaced by the payment card widget itself
 * (`useNativeButton: true`, matching the ticket-purchase precedent in
 * `TicketingPurchase.tsx`) — its own submit button drives the charge;
 * approval is reported via `onApprovedPayment`. Card/debit only for now — no
 * ACH. Pay Later never produces a payin config, so it always goes straight
 * to the submitted/thank-you state instead.
 */
function PaymentSection(
    {
        submitting,
        submitted,
        headingRef,
        paymentConfig,
        selectedTournaments,
        paidTournamentIds,
        paidTournaments,
        deferredTournaments,
        dueNowCents,
        deferredCents,
        payLaterEnabled,
        errors,
        turnstileContainerRef,
        turnstileToken,
        onTogglePaidTournament,
        onBack,
        onPayNow,
        onPayLater,
        onApprovedPayment,
    }: {
        submitting: boolean
        submitted: boolean
        headingRef: React.RefObject<HTMLHeadingElement | null>
        paymentConfig: PaymentComponentDescriptor | null
        selectedTournaments: Tournament[]
        paidTournamentIds: string[]
        paidTournaments: Tournament[]
        deferredTournaments: Tournament[]
        dueNowCents: number
        deferredCents: number
        payLaterEnabled: boolean
        errors: Record<string, string>
        turnstileContainerRef: React.RefObject<HTMLDivElement | null>
        turnstileToken: string | null
        onTogglePaidTournament: (tournamentId: string) => void
        onBack: () => void
        onPayNow: () => void
        onPayLater: () => void
        onApprovedPayment: (detail: ApprovedPaymentDetail) => void
    },
): ReactNode {
    const {
        paymentComponent,
        paymentComponentLoaded,
    } = usePaymentComponent(paymentConfig, {
        onApproved: onApprovedPayment,
    }, {
        useNativeButton: true,
        hideErrorMessage: true,
        acceptedMethods: {
            card: true,
            ach: false,
            applePay: false,
            paypal: false,
            venmo: false,
        },
    })

    const paymentSection = (children: ReactNode): ReactNode => (
        <Section
            title={'Payment'}
            icon={<CreditCardIcon aria-hidden={'true'} className={'size-3.5'}/>}
            testId={'registration-section-payment'}
            state={'active'}
            headingRef={headingRef}
        >
            {children}
        </Section>
    )

    if (submitted) {
        return paymentSection(
            <div
                role={'status'}
                aria-live={'polite'}
                aria-atomic={'true'}
                data-testid={'registration-payment-confirmation'}
                className={'flex flex-col gap-3 text-[14px] text-gray-700'}
            >
                <PaymentConfirmation paidTournaments={paidTournaments} deferredTournaments={deferredTournaments}/>
            </div>,
        )
    }

    if (paymentConfig != null) {
        return paymentSection(<>
            <p className={'text-[14px] text-gray-700'}>
                Enter your card to charge {formatCurrency(dueNowCents)}.
            </p>
            <div
                data-testid={'registration-payment-widget'}
                aria-busy={!paymentComponentLoaded}
                className={classNames(paymentComponentLoaded ? 'opacity-100' : 'opacity-0', 'relative transition-opacity duration-500')}
            >
                {paymentComponent}
            </div>
            {errors.submission != null && (
                <InputErrorLabel error={errors.submission} testId={'registration-payment-error'}/>
            )}
            <span role={'status'} aria-live={'polite'} className={'sr-only'}>
                {!paymentComponentLoaded ? 'Loading payment form…' : (submitting ? 'Processing…' : '')}
            </span>
        </>)
    }

    return paymentSection(<>
        <button
            type={'button'}
            disabled={submitting}
            onClick={onBack}
            data-testid={'registration-payment-back'}
            className={'-mx-2 -my-2 self-start px-2 py-2 text-[14px] font-medium text-gray-500 underline disabled:cursor-not-allowed disabled:opacity-50'}
        >
            ← Back to form
        </button>

        <ul data-testid={'registration-payment-summary'} className={'flex flex-col gap-3'}>
            {selectedTournaments.map((tournament) => (
                <li
                    key={tournament.id}
                    className={'flex items-center gap-3 text-[14px] text-gray-700'}
                >
                    {payLaterEnabled && (
                        <label className={'flex flex-1 cursor-pointer items-center gap-3 py-1'}>
                            <input
                                type={'checkbox'}
                                checked={paidTournamentIds.includes(tournament.id)}
                                onChange={() => {
                                    onTogglePaidTournament(tournament.id)
                                }}
                                data-testid={`registration-payment-checkbox-${tournament.id}`}
                                className={OPTION_INPUT_CLASS}
                            />
                            <span className={'flex-1'}>{tournament.name}</span>
                        </label>
                    )}
                    {!payLaterEnabled && <span className={'flex-1'}>{tournament.name}</span>}
                    <span>{tournament.entry_fee != null ? formatCurrency(tournament.entry_fee) : 'Fee not set'}</span>
                </li>
            ))}
        </ul>

        <div
            data-testid={'registration-payment-total'}
            className={'flex justify-between border-t border-gray-200 pt-3 text-[14px] font-semibold text-gray-900'}
        >
            <span>Due now</span>
            <span>{formatCurrency(dueNowCents)}</span>
        </div>

        {payLaterEnabled && deferredCents > 0 && (
            <p data-testid={'registration-payment-deferred'} className={'text-[14px] text-gray-500'}>
                {formatCurrency(deferredCents)} will be invoiced for later payment.
            </p>
        )}

        <div ref={turnstileContainerRef} data-testid={'registration-payment-captcha'}/>
        {errors.captcha_token != null && (
            <InputErrorLabel error={errors.captcha_token} testId={'registration-payment-captcha-error'}/>
        )}
        {errors.tournament_ids != null && (
            <InputErrorLabel error={errors.tournament_ids} testId={'registration-payment-tournaments-error'}/>
        )}
        {errors.paid_tournament_ids != null && (
            <InputErrorLabel error={errors.paid_tournament_ids} testId={'registration-payment-paid-tournaments-error'}/>
        )}
        {errors.submission != null && (
            <InputErrorLabel error={errors.submission} testId={'registration-payment-error'}/>
        )}

        <RegistrationButton
            size={'lg'}
            classes={['w-full cursor-pointer']}
            disabled={paidTournamentIds.length === 0 || turnstileToken == null || submitting}
            onClick={onPayNow}
            testId={'registration-pay-now'}
        >
            {submitting ? 'Processing…' : 'Pay Now'}
        </RegistrationButton>

        {payLaterEnabled && (
            <RegistrationButton
                outlined
                color={'secondary'}
                size={'lg'}
                classes={['w-full cursor-pointer']}
                disabled={turnstileToken == null || submitting}
                onClick={onPayLater}
                testId={'registration-pay-later'}
            >
                {submitting ? 'Submitting…' : 'Pay Later'}
            </RegistrationButton>
        )}

        <span role={'status'} aria-live={'polite'} className={'sr-only'}>
            {submitting ? 'Submitting…' : ''}
        </span>
    </>)
}

/**
 * Shown when the Coach step matches an existing Person: first the offer to
 * prefill their Team + Address, then — only if they say Yes — the OTP that
 * has to clear before any of it is handed over. No session or login is
 * created either way. The caller owns when this mounts, so `status` excludes
 * `idle` rather than rendering an empty card for it.
 */
function VerificationPrompt(
    {status, channel, firstName, code, setCode, errors, sending, checking, resendLockedUntil, onSend, onVerify, onContinueWithoutPrefill}: {
        status: Exclude<VerificationStatus, 'idle'>
        channel: 'sms' | 'email' | null
        firstName: string
        code: string
        setCode: (value: string) => void
        errors: Record<string, string>
        sending: boolean
        checking: boolean
        resendLockedUntil: number | null
        onSend: () => void
        onVerify: () => void
        onContinueWithoutPrefill: () => void
    },
): ReactNode {
    const resendDisabled = sending || (resendLockedUntil != null && Date.now() < resendLockedUntil)

    return <section
        data-testid={'registration-verification-prompt'}
        className={'flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6'}
    >
        {status === 'offered' && (
            <>
                <p className={'text-[14px] text-gray-700'} data-testid={'registration-verification-prefill-offer'}>
                    Hi {firstName}. It looks like you have registered with us before.
                    Would you like us to prefill your team and address?
                </p>
                {errors.verification != null && (
                    <InputErrorLabel error={errors.verification} testId={'registration-verification-error'}/>
                )}
                <div className={'flex gap-3'}>
                    <RegistrationButton
                        classes={['flex-1 cursor-pointer']}
                        disabled={sending}
                        onClick={onSend}
                        testId={'registration-verification-prefill-accept'}
                    >
                        {sending ? 'Sending…' : 'Yes'}
                    </RegistrationButton>
                    <RegistrationButton
                        outlined
                        classes={['flex-1 cursor-pointer']}
                        disabled={sending}
                        onClick={onContinueWithoutPrefill}
                        testId={'registration-verification-prefill-decline'}
                    >
                        No
                    </RegistrationButton>
                </div>
            </>
        )}
        {status === 'sent' && (
            <>
                <p className={'text-[14px] text-gray-700'}>
                    {channel === 'sms' ? 'We texted' : 'We emailed'} you a 6-digit verification code.
                </p>
                <Input
                    type={'text'}
                    title={'Verification Code'}
                    field={'verification_code'}
                    value={code}
                    setData={(_, value) => {
                        setCode(value)
                    }}
                    error={errors.code}
                    testId={'registration-verification-code'}
                    maxlength={6}
                />
                <RegistrationButton
                    classes={['w-full cursor-pointer']}
                    disabled={code.length !== 6 || checking}
                    onClick={onVerify}
                    testId={'registration-verification-submit'}
                >
                    {checking ? 'Verifying…' : 'Verify'}
                </RegistrationButton>
                <RegistrationButton
                    outlined
                    classes={['w-full cursor-pointer']}
                    disabled={resendDisabled}
                    onClick={onSend}
                    testId={'registration-verification-resend'}
                >
                    Resend code
                </RegistrationButton>
                {errors.verification != null && (
                    <InputErrorLabel error={errors.verification} testId={'registration-verification-error'}/>
                )}
                <button
                    type={'button'}
                    data-testid={'registration-verification-skip'}
                    className={'text-[14px] font-medium text-gray-500 underline'}
                    onClick={onContinueWithoutPrefill}
                >
                    Skip and enter manually
                </button>
            </>
        )}
    </section>
}

/**
 * An operator-authored option-based field (radio = single-select, checkbox =
 * multi-select). Shares one rendering path since the two differ only in input
 * type and selection semantics.
 */
function OptionGroup(
    {
        inputType,
        name,
        label,
        options,
        testId,
        isChecked,
        onToggle,
        error,
        required,
    }: {
        inputType: 'radio' | 'checkbox'
        name: string
        label: string
        options: {value: string, label: string}[]
        testId: string
        isChecked: (value: string) => boolean
        onToggle: (value: string) => void
        error?: string | null
        required?: boolean
    },
): ReactNode {
    const errorId = `${testId}-error`

    return <fieldset aria-required={required || undefined} aria-describedby={error != null ? errorId : undefined}>
        <legend className={'form-control-label'}>{label}</legend>
        <div className={'flex flex-col gap-2'}>
            {options.map((option) => (
                <label key={option.value} className={'flex cursor-pointer items-center gap-2 text-[14px] text-gray-700'}>
                    <input
                        type={inputType}
                        name={name}
                        value={option.value}
                        checked={isChecked(option.value)}
                        onChange={() => {
                            onToggle(option.value)
                        }}
                        className={OPTION_INPUT_CLASS}
                        data-testid={`${testId}-${option.value}`}
                    />
                    {option.label}
                </label>
            ))}
        </div>
        <InputErrorLabel error={error} id={errorId} testId={errorId}/>
    </fieldset>
}

function CustomField(
    {field, value, setValue, error}: {
        field: RegistrationFormField
        value: FieldValue | undefined
        setValue: (key: string, value: FieldValue) => void
        error?: string
    },
): ReactNode {
    const key = `custom_${field.id}`
    const options = field.options ?? []
    const testId = `registration-custom-field-${field.id}`
    const stringValue = typeof value === 'string' ? value : ''
    const label = customFieldDisplayLabel(field)
    const isRequired = field.validations?.required === true

    const dropdownOptions = useMemo(
        () => (field.options ?? []).map((option) => new KeyValuePair(option, option)),
        [field.options],
    )

    if (field.type === 'textarea') {
        const errorId = `${testId}-error`

        return <div className={'block'}>
            <label className={'form-control-label'} htmlFor={key}>{label}</label>
            <textarea
                id={key}
                name={key}
                data-testid={testId}
                placeholder={field.placeholder ?? undefined}
                className={'form-control'}
                value={stringValue}
                aria-required={isRequired || undefined}
                aria-invalid={error != null || undefined}
                aria-describedby={error != null ? errorId : undefined}
                onChange={(event) => {
                    setValue(key, event.target.value)
                }}
            />
            <InputErrorLabel error={error} id={errorId} testId={errorId}/>
        </div>
    }

    if (field.type === 'dropdown') {
        return <SelectDropdown
            type={'native'}
            title={label}
            id={key}
            options={dropdownOptions}
            value={stringValue}
            setData={setValue}
            field={key}
            error={error}
            required={isRequired}
            testId={testId}
        />
    }

    if (field.type === 'radio') {
        return <OptionGroup
            inputType={'radio'}
            name={key}
            label={label}
            options={options.map((option) => ({value: option, label: option}))}
            testId={testId}
            isChecked={(option) => value === option}
            onToggle={(option) => {
                setValue(key, option)
            }}
            error={error}
            required={isRequired}
        />
    }

    if (field.type === 'checkbox') {
        const selected = Array.isArray(value) ? value : []

        return <OptionGroup
            inputType={'checkbox'}
            name={key}
            label={label}
            options={options.map((option) => ({value: option, label: option}))}
            testId={testId}
            isChecked={(option) => selected.includes(option)}
            onToggle={(option) => {
                const next = selected.includes(option)
                    ? selected.filter((existing) => existing !== option)
                    : [...selected, option]
                setValue(key, next)
            }}
            error={error}
            required={isRequired}
        />
    }

    return <Input
        type={'text'}
        title={label}
        field={key}
        placeholder={field.placeholder ?? undefined}
        value={stringValue}
        setData={setValue}
        error={error}
        required={isRequired}
        testId={testId}
    />
}
